import { ApiResponse } from 'apisauce';
import { cancelTokenFromSignal } from '../../../utils/axios.utils';
import { restApi } from '../../api';
import {
  CreateTargetInput,
  CreateTargetOutput,
  FindBreakdownDimensionsOutput,
  FindBreakdownItemsOutput,
  FindBreakdownItemsParams,
  FindOrderTargetsOutput,
  FindTargetBreakdownsOutput,
  FindTargetDetailOutput,
  FindTargetDimensionsOutput,
  FindTargetMaxConversionOutput,
  FindTargetMaxConversionParams,
  FindTargetMethodOptionsOutput,
  FindTargetMethodOptionsParams,
  FindTargetStatusGroupCountsOutput,
  FindTargetStatusGroupCountsParams,
  FindTargetTotalsOutput,
  FindTargetTotalsParams,
  FindTargetsCountParams,
  FindTargetsOutput,
  FindTargetsParams,
  UpdateBreakdownItemInput,
  UpdateTargetCriteriaInput,
  UpdateTargetDimensionsInput,
  UpdateTargetGeneralDataInput,
  UpdateTargetMethodInput,
  UpdateTargetParameterInput,
  UpdateTargetScheduledDateParams,
  UpdateTargetStatusParams,
  UpdateTargetsStatusParams,
} from './target.repository.types';
import { TargetStatusGroup, TargetType } from './target.types';

const TARGETS_PATH = 'targets';

const ORDERS_PATH = 'orders';

const PATH_BY_TYPE: Record<TargetType, string> = {
  [TargetType.BRAND_TO_SALES_REPRESENTATIVE]: 'targets/sales-representatives',
  [TargetType.BRAND_TO_CUSTOMER]: 'targets/customers',
  [TargetType.STORE_TO_ITSELF]: 'targets/store',
};

const STATUS_GROUP_COUNTS_PATH = 'status-group-counts';
const MAX_CONVERSION_PATH = 'max-conversion';
const METHOD_OPTIONS_PATH = 'methods';
const SCHEDULED_DATE_PATH = 'scheduled-date';

const STATUS_PATH = 'status';
const TOTALS_PATH = 'totals';

const BREAKDOWNS_PATH = 'breakdowns';

const PARAMETERS_PATH = 'parameters';

const CRITERIA_PATH = 'criteria';

const GENERAL_DATA_PATH = 'general-data';

const DIMENSIONS_PATH = 'dimensions';

const METHOD_PATH = 'method';
const BREAKDOWN_ITEMS_SEARCH_PATH = 'breakdown-items/search';

export const TARGETS_LIST_CACHE_KEY = 'targets-list';
export const TARGETS_STATUS_GROUP_COUNTS_CACHE_KEY = 'targets-status-group-counts';
export const TARGET_DETAIL_CACHE_KEY = 'target-detail';
export const TARGET_BREAKDOWNS_CACHE_KEY = 'target-breakdowns';
export const TARGET_DIMENSIONS_CACHE_KEY = 'target-dimensions';
export const TARGETS_MAX_CONVERSION_CACHE_KEY = 'targets-max-conversion';
export const TARGETS_METHOD_OPTIONS_CACHE_KEY = 'targets-method-options';
export const TARGETS_TOTALS_CACHE_KEY = 'targets-totals';
export const ORDER_TARGETS_CACHE_KEY = 'order-targets';

export const findTargets = ({ type, ...params }: FindTargetsParams, signal?: AbortSignal) =>
  restApi.get<FindTargetsOutput>(PATH_BY_TYPE[type], params, {
    cancelToken: cancelTokenFromSignal(signal)?.token,
  });

export const findOrderTargets = (orderId: string, signal?: AbortSignal) =>
  restApi.get<FindOrderTargetsOutput>(`${ORDERS_PATH}/${TARGETS_PATH}/${orderId}`, undefined, {
    cancelToken: cancelTokenFromSignal(signal)?.token,
  });

export const createTarget = (input: CreateTargetInput) =>
  restApi.post<CreateTargetOutput>(TARGETS_PATH, input);

export const findBreakdownDimensions = () =>
  restApi.get<FindBreakdownDimensionsOutput>(`${TARGETS_PATH}/breakdown-dimensions`);

export const findBreakdownItems = (params: FindBreakdownItemsParams, signal?: AbortSignal) =>
  restApi.post<FindBreakdownItemsOutput>(`${TARGETS_PATH}/${BREAKDOWN_ITEMS_SEARCH_PATH}`, params, {
    cancelToken: cancelTokenFromSignal(signal)?.token,
  });

export const findTargetStatusGroupCounts = (
  { type, ...params }: FindTargetStatusGroupCountsParams,
  signal?: AbortSignal,
) =>
  restApi.get<FindTargetStatusGroupCountsOutput>(
    `${PATH_BY_TYPE[type]}/${STATUS_GROUP_COUNTS_PATH}`,
    params,
    { cancelToken: cancelTokenFromSignal(signal)?.token },
  );

export const findTargetTotals = (
  { type, ...params }: FindTargetTotalsParams,
  signal?: AbortSignal,
) =>
  restApi.get<FindTargetTotalsOutput>(`${PATH_BY_TYPE[type]}/${TOTALS_PATH}`, params, {
    cancelToken: cancelTokenFromSignal(signal)?.token,
  });

export const findTargetMaxConversion = (
  { type }: FindTargetMaxConversionParams,
  signal?: AbortSignal,
) =>
  restApi.get<FindTargetMaxConversionOutput>(
    `${PATH_BY_TYPE[type]}/${MAX_CONVERSION_PATH}`,
    undefined,
    { cancelToken: cancelTokenFromSignal(signal)?.token },
  );

export const findTargetMethodOptions = (
  { type, ...params }: FindTargetMethodOptionsParams,
  signal?: AbortSignal,
) =>
  restApi.get<FindTargetMethodOptionsOutput>(
    `${PATH_BY_TYPE[type]}/${METHOD_OPTIONS_PATH}`,
    params,
    { cancelToken: cancelTokenFromSignal(signal)?.token },
  );

const deriveTargetsTotal = (
  counts: FindTargetStatusGroupCountsOutput,
  statusGroup?: TargetStatusGroup,
): number => {
  if (!statusGroup) {
    return counts.total;
  }
  return counts[statusGroup];
};

export const findTargetsCount = async (
  { type, ...params }: FindTargetsCountParams,
  signal?: AbortSignal,
): Promise<ApiResponse<number>> => {
  const response = await findTargetStatusGroupCounts({ type, ...params }, signal);

  if (response.ok && response.data) {
    return {
      ...response,
      data: deriveTargetsTotal(response.data, params.statusGroup),
    } as ApiResponse<number>;
  }

  return response as ApiResponse<number>;
};

export const findTargetDetail = (targetId: string, signal?: AbortSignal) =>
  restApi.get<FindTargetDetailOutput>(
    `${TARGETS_PATH}/${targetId}`,
    {},
    { cancelToken: cancelTokenFromSignal(signal)?.token },
  );

export const findTargetBreakdowns = (targetId: string, signal?: AbortSignal) =>
  restApi.get<FindTargetBreakdownsOutput>(
    `${TARGETS_PATH}/${targetId}/${BREAKDOWNS_PATH}`,
    {},
    { cancelToken: cancelTokenFromSignal(signal)?.token },
  );

export const updateBreakdownItem = ({ targetId, breakdownId, ...body }: UpdateBreakdownItemInput) =>
  restApi.patch<void>(`${TARGETS_PATH}/${targetId}/${BREAKDOWNS_PATH}/${breakdownId}`, body);

export const updateTargetParameter = ({ targetId, type, ...body }: UpdateTargetParameterInput) =>
  restApi.put<void>(`${TARGETS_PATH}/${targetId}/${PARAMETERS_PATH}/${type}`, body);

export const findTargetDimensions = (targetId: string, signal?: AbortSignal) =>
  restApi.get<FindTargetDimensionsOutput>(
    `${TARGETS_PATH}/${targetId}/${DIMENSIONS_PATH}`,
    {},
    { cancelToken: cancelTokenFromSignal(signal)?.token },
  );

export const updateTargetDimensions = ({ targetId, ...body }: UpdateTargetDimensionsInput) =>
  restApi.put<void>(`${TARGETS_PATH}/${targetId}/${DIMENSIONS_PATH}`, body);

export const updateTargetCriteria = ({ targetId, ...body }: UpdateTargetCriteriaInput) =>
  restApi.put<void>(`${TARGETS_PATH}/${targetId}/${CRITERIA_PATH}`, body);

export const updateTargetGeneralData = ({ targetId, ...body }: UpdateTargetGeneralDataInput) =>
  restApi.put<void>(`${TARGETS_PATH}/${targetId}/${GENERAL_DATA_PATH}`, body);

export const updateTargetMethod = ({ targetId, methodId }: UpdateTargetMethodInput) =>
  restApi.put<void>(`${TARGETS_PATH}/${targetId}/${METHOD_PATH}`, { methodId });

export const updateTargetScheduledDate = ({
  targetId,
  scheduledDate,
}: UpdateTargetScheduledDateParams) =>
  restApi.patch<void>(`targets/${targetId}/${SCHEDULED_DATE_PATH}`, { scheduledDate });

export const updateTargetStatus = ({ targetId, status, lossReason }: UpdateTargetStatusParams) =>
  restApi.patch<void>(`targets/${targetId}/${STATUS_PATH}`, { status, lossReason });

export const updateTargetsStatus = (params: UpdateTargetsStatusParams) =>
  restApi.patch<void>(`targets/batch/${STATUS_PATH}`, params);
